import { stringify as stringifyCsvRows } from 'csv-stringify';
import { Form, setIn } from 'formik';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAddGuestsStatic } from './AddGuestsContext';
import {
    resolveGuestImportRegions,
    buildTemplateHeaderRow,
    buildTemplateGuestRowsWithoutData,
    buildTemplateGuestRowsWithData,
    buildRawNextRoomsFromGuestsImport,
    clearFormikErrorsForGuest,
    convertBirthdaysToSystemFormat,
    removeFormikErrorsGuestEntry,
    resolveGuestImportValuesToStaticMapKeys,
} from './addGuestsUtilities';
import { confirmModal } from '../../modals';
import { getCountryRegions, translate, triggerCsvFileDownload } from '../../utils';
import { AddGuestsTable } from './AddGuestsTable';
import { AddGuestsPageActions } from './AddGuestsPageActions';

export const AddGuestsContent = memo(function AddGuestsContent({ errors, rooms, setErrors, setFieldValue, submitForm, setFormikStatus, formikStatus }) {
    const {
        reservationCode,
        guestColumnKeys,
        guestColumnKeysMap,
        initialRegionMaps,
        onRegisterGuestInitialValues,
        clientTitlesMap,
        countriesMap,
        languageMap,
        vipStatusMap,
        maxGuestsPerRoom,
    } = useAddGuestsStatic();

    const [regionMaps, setRegionMaps] = useState(initialRegionMaps);
    const roomsRef = useRef(rooms);

    const handleAddGuestsFormSubmit = useCallback(async (event) => {
        event.preventDefault();
        await setFieldValue('rooms', roomsRef.current, false);
        await submitForm();
    }, [setFieldValue, submitForm]);

    const handleAddGuest = useCallback((roomIndex) => {
        setFieldValue('rooms', (prevRooms) => {
            return prevRooms.map((room, currentRoomIndex) => {
                if (currentRoomIndex !== roomIndex) return room;
                return { ...room, guests: [...room.guests, createEmptyGuest(guestColumnKeys)] };
            });
        }, false);
    }, [guestColumnKeys, setFieldValue]);

    const handleRemoveGuest = useCallback((roomIndex, guestIndex) => {
        const roomAtIndex = roomsRef.current[roomIndex];
        const shouldRemoveGuestRow = roomAtIndex.guests.length > 1;
        let remainingGuests = [];

        setFieldValue('rooms', (prevRooms) => {
            return prevRooms.map((room, currentRoomIndex) => {
                if (currentRoomIndex !== roomIndex) return room;

                if (shouldRemoveGuestRow) {
                    remainingGuests = room.guests.filter((_, currentGuestIndex) => currentGuestIndex !== guestIndex);
                    return { ...room, guests: remainingGuests };
                }

                return {
                    ...room,
                    guests: room.guests.map((guest, currentGuestIndex) => currentGuestIndex === guestIndex ? createEmptyGuest(guestColumnKeys) : guest
                    ),
                };
            });
        }, false);

        if (shouldRemoveGuestRow) {
            let nextErrors = removeFormikErrorsGuestEntry(errors, roomIndex, guestIndex);

            const removedGuestClientKey = roomAtIndex.guests[guestIndex]?.clientKey ?? '';
            const hasOneDuplicateClientInRoom = removedGuestClientKey !== '' && roomAtIndex.guests.filter((guest) => guest.clientKey === removedGuestClientKey).length === 2;
            if (hasOneDuplicateClientInRoom) {
                const duplicateClientIndex = remainingGuests.findIndex((guest) => guest.clientKey === removedGuestClientKey);
                nextErrors = setIn(nextErrors, `rooms.${roomIndex}.guests.${duplicateClientIndex}.clientKey`, undefined);
            }

            setErrors(nextErrors);
        } else {
            setErrors(clearFormikErrorsForGuest(errors, roomIndex, guestIndex));
        }
    }, [errors, guestColumnKeys, setErrors, setFieldValue]);

    const handleRegionMapsUpdate = useCallback((countryCode, regionsMap) => {
        setRegionMaps(prevRegionMaps => ({ ...prevRegionMaps, [countryCode]: regionsMap }));
    }, [setRegionMaps]);

    const handleSelectClientSuggestion = useCallback(async (roomIndex, guestIndex, selectedClient) => {
        if (selectedClient.country_code && regionMaps[selectedClient.country_code] === undefined) {
            const regionsMap = await getCountryRegions(selectedClient.country_code, { asMap: true });
            handleRegionMapsUpdate(selectedClient.country_code, regionsMap);
        }

        setFieldValue('rooms', (prevRooms) => {
            const existingGuest = findGuestWithClientKey(prevRooms, selectedClient.client_key);

            const guestFieldUpdates = { clientKey: selectedClient.client_key };
            if (existingGuest) {
                for (const guestColumnKey of guestColumnKeys) {
                    guestFieldUpdates[guestColumnKey] = existingGuest[guestColumnKey] ?? '';
                }
            } else {
                for (const guestColumnKey of guestColumnKeys) {
                    if (guestColumnKey === 'clientTitleId') {
                        guestFieldUpdates[guestColumnKey] = selectedClient.title_key ?? '';
                    } else if (guestColumnKey === 'vipStatusId') {
                        guestFieldUpdates[guestColumnKey] = selectedClient.vip_key ?? '';
                    } else if (guestColumnKey === 'country') {
                        guestFieldUpdates[guestColumnKey] = selectedClient.country_code ?? '';
                    } else if (guestColumnKey === 'nationality') {
                        guestFieldUpdates[guestColumnKey] = selectedClient.nationality_country_code ?? '';
                    } else {
                        guestFieldUpdates[guestColumnKey] = selectedClient[guestColumnKeysMap[guestColumnKey]] ?? '';
                    }
                }
            }

            onRegisterGuestInitialValues(selectedClient.client_key, guestFieldUpdates);

            return prevRooms.map((room, currentRoomIndex) => {
                if (currentRoomIndex !== roomIndex) return room;
                return {
                    ...room,
                    guests: room.guests.map((guest, currentGuestIndex) => {
                        if (currentGuestIndex !== guestIndex) return guest;

                        return { ...guest, ...guestFieldUpdates };
                    }),
                };
            });
        }, false);

        setErrors(clearFormikErrorsForGuest(errors, roomIndex, guestIndex));
    }, [errors, guestColumnKeys, guestColumnKeysMap, handleRegionMapsUpdate, onRegisterGuestInitialValues, regionMaps, setErrors, setFieldValue]);

    const downloadTemplate = useCallback((shouldIncludeGuestData, maps = {}) => {
        const headerRow = buildTemplateHeaderRow(guestColumnKeys);
        const dataRows = shouldIncludeGuestData
            ? buildTemplateGuestRowsWithData(roomsRef.current, guestColumnKeys, maps)
            : buildTemplateGuestRowsWithoutData(roomsRef.current, guestColumnKeys);
        const csvContent = stringifyCsvRows([headerRow, ...dataRows], { delimiter: ';' });
        const sanitizedReservationCode = reservationCode.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');

        triggerCsvFileDownload(csvContent, `${sanitizedReservationCode}-guests.csv`);
    }, [guestColumnKeys, reservationCode]);

    const handleDownloadEmptyTemplate = useCallback(() => downloadTemplate(false), [downloadTemplate]);
    const handleDownloadTemplateWithGuests = useCallback(() => downloadTemplate(true, { clientTitlesMap, countriesMap, languageMap, vipStatusMap, regionMaps }), [clientTitlesMap, countriesMap, downloadTemplate, languageMap, regionMaps, vipStatusMap]);

    const handleUploadGuestsData = useCallback(async (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        const confirmModalChoice = await confirmModal(
            translate('frontend', 'ADD_GUESTS_UPLOAD_CONFIRM_TITLE'),
            translate('frontend', 'ADD_GUESTS_UPLOAD_CONFIRM_MESSAGE', { fileName: selectedFile.name }),
        );
        if (confirmModalChoice !== 'confirm') return;

        setFormikStatus(undefined);
        setErrors({});

        let fileText;
        try {
            fileText = await selectedFile.text();
        } catch {
            setFormikStatus({ unknownErrors: [translate('frontend', 'ADD_GUESTS_UPLOAD_READ_ERROR')] });
            return;
        }

        const importResult = buildRawNextRoomsFromGuestsImport(fileText, guestColumnKeys, roomsRef.current, maxGuestsPerRoom);

        if (!importResult.ok) {
            setFormikStatus({ unknownErrors: [translate('frontend', importResult.errorTranslationKey)] });
            return;
        }

        const { rawNextRooms, invalidRoomNameAndCategoryLabels, overflowedRoomNameAndCategoryLabels } = importResult;

        const { rooms: nextRoomsAfterBirthdaysConverted, fieldErrors: fieldErrorsAfterBirthdaysConverted } = convertBirthdaysToSystemFormat(rawNextRooms);
        const { rooms: nextRoomsAfterMappingResolved, fieldErrors: fieldErrorsAfterMappingResolved } = resolveGuestImportValuesToStaticMapKeys(
            nextRoomsAfterBirthdaysConverted,
            { clientTitlesMap, countriesMap, languageMap, vipStatusMap },
            fieldErrorsAfterBirthdaysConverted,
        );

        const countriesSet = new Set();
        for (const nextRoom of nextRoomsAfterMappingResolved) {
            for (const guest of nextRoom.guests) {
                if (guest.country !== '') countriesSet.add(guest.country);
            }
        }

        const _regionMaps = { ...regionMaps };

        for (const country of countriesSet) {
            if (_regionMaps[country] !== undefined) continue;
            const regionsMap = await getCountryRegions(country, { asMap: true });
            _regionMaps[country] = regionsMap;
        }

        const { rooms: nextRooms, fieldErrors } = resolveGuestImportRegions(nextRoomsAfterMappingResolved, _regionMaps, fieldErrorsAfterMappingResolved);

        if (Object.keys(_regionMaps).length > Object.keys(regionMaps).length) {
            setRegionMaps(_regionMaps);
        }

        setFieldValue('rooms', nextRooms, false);
        setErrors(fieldErrors);

        if (invalidRoomNameAndCategoryLabels.size > 0 || overflowedRoomNameAndCategoryLabels.size > 0) {
            setFormikStatus({
                unknownErrors: [],
                invalidRoomNameAndCategoryLabels: Array.from(invalidRoomNameAndCategoryLabels),
                overflowedRoomNameAndCategoryLabels: Array.from(overflowedRoomNameAndCategoryLabels),
            });
        }
    }, [
        clientTitlesMap,
        countriesMap,
        guestColumnKeys,
        languageMap,
        maxGuestsPerRoom,
        regionMaps,
        setErrors,
        setFieldValue,
        setFormikStatus,
        setRegionMaps,
        vipStatusMap,
    ]);

    useEffect(() => {
        roomsRef.current = rooms;
    }, [rooms]);

    return (
        <section className="content">
            <AddGuestsPageActions
                onUploadGuestsData={handleUploadGuestsData}
                onDownloadEmptyTemplate={handleDownloadEmptyTemplate}
                onDownloadTemplateWithGuests={handleDownloadTemplateWithGuests}
            />

            <div className="box box-solid">
                <div className="box-header">
                    <h3 className="box-title">
                        {translate('frontend', 'RESERVATION_TOP')} {reservationCode}
                    </h3>
                </div>

                <div className="box-body">
                    <Form id="addGuestsFormId" onSubmit={handleAddGuestsFormSubmit}>
                        {formikStatus?.unknownErrors?.length > 0 ? (
                            <div className="alert alert-danger mb-4">
                                <div className="alert-body">
                                    {formikStatus.unknownErrors.map((message, index) => (
                                        <p key={index}>{message}</p>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {formikStatus?.invalidRoomNameAndCategoryLabels?.length > 0 ? (
                            <div className="alert alert-danger mb-4">
                                <div className="alert-body">
                                    <p>{translate('frontend', 'ADD_GUESTS_UPLOAD_INVALID_ROOM_CATEGORY')}</p>

                                    <ul className="mb-0">
                                        {formikStatus.invalidRoomNameAndCategoryLabels.map((roomCategoryLabel, index) => (
                                            <li key={index}>{roomCategoryLabel}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : null}

                        {formikStatus?.overflowedRoomNameAndCategoryLabels?.length > 0 ? (
                            <div className="alert alert-danger mb-4">
                                <div className="alert-body">
                                    <p>{translate('frontend', 'ADD_GUESTS_UPLOAD_ROOM_CATEGORY_OVERFLOW', { max: maxGuestsPerRoom })}</p>

                                    <ul className="mb-0">
                                        {formikStatus.overflowedRoomNameAndCategoryLabels.map((roomCategoryLabel, index) => (
                                            <li key={index}>{roomCategoryLabel}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : null}

                        <AddGuestsTable
                            errors={errors}
                            rooms={rooms}
                            setErrors={setErrors}
                            setFieldValue={setFieldValue}
                            regionMaps={regionMaps}
                            onRegionMapsUpdate={handleRegionMapsUpdate}
                            onAddGuest={handleAddGuest}
                            onRemoveGuest={handleRemoveGuest}
                            onSelectClientSuggestion={handleSelectClientSuggestion}
                        />
                    </Form>

                    <div className="hstack justify-center px-2 pt-3">
                        <span className="label add-guests__over-occupancy-label">
                            {translate('frontend', 'ADD_GUESTS_OVER_OCCUPANCY_LEGEND')}
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
});

function createEmptyGuest(guestColumnKeys) {
    const guest = { clientKey: '' };
    guestColumnKeys.forEach(guestColumnKey => guest[guestColumnKey] = '');

    return guest;
}

/**
 * @param {Array<{ guests: Array<{ clientKey: string }> }>} rooms
 * @param {string} clientKey
 * @returns {Record<string, unknown> | null}
 */
function findGuestWithClientKey(rooms, clientKey) {
    for (const room of rooms) {
        for (const guest of room.guests) {
            if (guest.clientKey === clientKey) {
                return guest;
            }
        }
    }

    return null;
}
